"use client";

import Link from "next/link";
import { useEffect } from "react";
import posthog from "posthog-js";
import { buttonClass } from "@/components/ui";

/**
 * CM-285: root not-found boundary. Next.js renders this for every server-side
 * `notFound()` call (jobs/[id], organizations/[key], guides/[slug], etc.) and
 * for any unmatched route — previously the framework default, invisible to
 * analytics. `capture_pageview:true` in PostHogProvider already fires an
 * untagged $pageview before this mounts, so a second, explicitly-tagged
 * $pageview is how `page_status:"not_found"` becomes queryable without
 * touching the shared PageviewTracker (every other route stays untouched).
 */
function NotFoundPageview() {
  useEffect(() => {
    if (!posthog.__loaded) return;
    posthog.capture("$pageview", {
      $current_url: window.location.origin + window.location.pathname,
      page_status: "not_found",
    });
  }, []);
  return null;
}

export default function NotFound() {
  return (
    <main className="mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center sm:py-32">
      <NotFoundPageview />
      <p className="telex text-[11px] uppercase tracking-wide text-brand-700">
        404
      </p>
      <h1 className="display mt-3 text-3xl font-semibold text-ink sm:text-4xl">
        That posting is gone.
      </h1>
      <p className="mt-4 text-[15px] leading-relaxed text-neutral-600">
        The link may be old, or the vacancy has closed. The board is full of
        current openings at UN agencies, development banks, EU institutions,
        IGOs and INGOs.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Link href="/jobs" className={buttonClass("primary")}>
          Browse all live vacancies
        </Link>
        <Link href="/" className={buttonClass("secondary")}>
          Back to homepage
        </Link>
      </div>
    </main>
  );
}
